<template>
  <form @submit.prevent="submit">
    <FormGroup>
      <template v-if="!emailSent">
        <Text variant="h1">Enter your email</Text>

        <FormField label="Email" :error="form.fieldErrors('email')">
          <Input v-model="form.fields.email" name="email" type="email" placeholder="Type your email" />
        </FormField>

        <Button type="submit" :loading="form.submitting">Reset password</Button>
      </template>

      <template v-else>
        <View direction="column" gap="sm">
          <Text variant="h1">Check your email</Text>
          <Text variant="body-large" color="secondary">We've sent a password reset link to</Text>
          <Text variant="h4">{{ form.fields.email }}</Text>
        </View>
      </template>

      <Text variant="h4" :align="emailSent ? 'start' : 'center'">
        <Link color="accent" :to="backToSignInRoute">Back to Log in</Link>
      </Text>
    </FormGroup>
  </form>
</template>

<script setup lang="ts">
import { email, required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { onMounted, ref, watch } from 'vue';
import { RouteLocationRaw, useRoute } from 'vue-router';

import { Button, FormField, FormGroup, Input, Link, Text, View, useForm } from '@tallo/design-system';
import { UserRole } from '@tallo/user';

import { useAuthStore } from '../auth.store';

const props = defineProps<{
  backToSignInRoute: RouteLocationRaw;
  forRole: UserRole;
}>();

const route = useRoute();
const userEmail = route.query.email as string;
const resetLinkSent = route.query.sent as string;

const authStore = useAuthStore();
const form = useForm(
  {
    email: '',
  },
  {
    email: { required, email },
  },
);

const emailSent = ref(false);

async function submit() {
  form.submit(async () => {
    try {
      await authStore.sendPasswordResetLink(form.fields.email, props.forRole);
      emailSent.value = true;
    } catch (error) {
      if (error instanceof AxiosError) {
        form.setFieldError('email', error.response?.data?.message);
      }
    }
  });
}

watch(form.fields, () => form.clearValidationErrors());

onMounted(() => {
  if (resetLinkSent === 'true') {
    emailSent.value = true;
  }

  if (userEmail) {
    form.fields.email = userEmail;
  }
});
</script>
